import * as React from "react";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// ViewfinderFrame — camera-bracket frame highlighting a tactical moment.
// Use to frame imagery, feature content, or wrap any "this is the moment"
// gesture. Renders a 4-corner bracket; children render inside.
// ════════════════════════════════════════════════════════════════════════

export interface ViewfinderFrameProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Inset of the corner brackets (px). */
  inset?: number;
  /** Length of each corner arm (px). */
  arm?: number;
  /** Stroke weight (px). */
  weight?: number;
  /** Bracket color. Defaults to currentColor. */
  color?: string;
  /** Optional timecode chip shown bottom-left. */
  label?: React.ReactNode;
}

export const ViewfinderFrame = React.forwardRef<
  HTMLDivElement,
  ViewfinderFrameProps
>(
  (
    {
      inset = 14,
      arm = 28,
      weight = 4,
      color = "currentColor",
      label,
      className,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const armStyle: React.CSSProperties = {
      position: "absolute",
      width: arm,
      height: arm,
      borderColor: color,
      borderStyle: "solid",
      borderRadius: 2,
    };
    return (
      <div
        ref={ref}
        className={cn("relative w-full text-ink", className)}
        style={style}
        {...rest}
      >
        {/* corners */}
        <span
          style={{
            ...armStyle,
            top: inset,
            left: inset,
            borderWidth: `${weight}px 0 0 ${weight}px`,
          }}
        />
        <span
          style={{
            ...armStyle,
            top: inset,
            right: inset,
            borderWidth: `${weight}px ${weight}px 0 0`,
          }}
        />
        <span
          style={{
            ...armStyle,
            bottom: inset,
            left: inset,
            borderWidth: `0 0 ${weight}px ${weight}px`,
          }}
        />
        <span
          style={{
            ...armStyle,
            bottom: inset,
            right: inset,
            borderWidth: `0 ${weight}px ${weight}px 0`,
          }}
        />

        {/* content */}
        {children}

        {/* timecode label */}
        {label != null && (
          <span
            className="absolute font-mono text-[10px] font-semibold tracking-mono uppercase rounded-sm"
            style={{
              left: inset + weight + 2,
              bottom: inset + weight + 2,
              padding: "4px 8px",
              background: "var(--ink-primary)",
              color: "var(--accent)",
            }}
          >
            {label}
          </span>
        )}
      </div>
    );
  },
);
ViewfinderFrame.displayName = "ViewfinderFrame";
